import {
  Badge,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Title,
} from "@clipboard-health/ui-components";
import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { APP_V2_USER_EVENTS, openBrowser } from "@src/appV2/lib";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { isZendeskMessagingUnread } from "@src/appV2/lib/ZendeskMessaging/utils";
import { USER_EVENTS } from "@src/constants";
import { useHistory, useRouteMatch } from "react-router-dom";

import { TERMS_OF_SERVICE_URL } from "../../constants/urls";
import { useHasUpcomingOrInProgressShift } from "../hooks/useHasUpcomingOrInProgressShift";
import { useIsTimeclockInfoWhenContactingSupportEnabled } from "../hooks/useIsTimeclockInfoWhenContactingSupportEnabled";
import { logAccountPageMenuItemTapped } from "../utils";

interface AccountsPageSupportSectionProps {
  unreadCount: number;
  onGetHelpClick: () => void;
  onContactSupportClick: () => void;
}

/**
 * SupportSection Component
 *
 * Displays support-related menu items on the Account page.
 *
 * "Get Help" Button Behavior:
 * The button shows different flows based on feature flag and shift status:
 * - If feature flag is enabled AND worker has an in-progress shift:
 *   Opens GetHelpDuringShiftBottomSheet (intervention flow)
 * - Otherwise: Navigates to legacy /needhelp page
 *
 * Feature Flag: The intervention flow is gated behind the LaunchDarkly feature flag
 * "2025-18-orbit-timeclock-info-when-contacting-support". Both the feature flag AND
 * an in-progress shift must be true for the intervention to appear.
 */
export function AccountsPageSupportSection(props: AccountsPageSupportSectionProps) {
  const { unreadCount, onGetHelpClick, onContactSupportClick } = props;
  const { isEmbedded } = useEmbeddedMode();

  // Check if worker has an in-progress shift to determine which flow to show
  const hasUpcomingOrInProgressShift = useHasUpcomingOrInProgressShift();
  // Check if the feature flag is enabled for this user
  const isTimeclockInfoEnabled = useIsTimeclockInfoWhenContactingSupportEnabled();
  const history = useHistory();
  const match = useRouteMatch();

  return (
    <>
      <Title semibold component="h4">
        Support
      </Title>
      {!isEmbedded && (
        <ListItem divider>
          <ListItemButton
            onClick={async () => {
              logAccountPageMenuItemTapped(APP_V2_USER_EVENTS.OPENED_TERMS_OF_SERVICE);
              await openBrowser(TERMS_OF_SERVICE_URL);
            }}
          >
            <ListItemIcon iconType="info" />
            <ListItemText primary="Terms of Service" />
          </ListItemButton>
        </ListItem>
      )}

      <ListItem
        divider
        secondaryAction={
          isZendeskMessagingUnread() ? <Badge color="error" count={unreadCount} /> : undefined
        }
      >
        <ListItemButton
          onClick={async () => {
            logAccountPageMenuItemTapped(APP_V2_USER_EVENTS.OPENED_CONTACT_SUPPORT);
            // Only show intervention if feature flag is enabled AND shift is in progress
            if (isTimeclockInfoEnabled && hasUpcomingOrInProgressShift) {
              onContactSupportClick();
            } else {
              await openZendeskMessaging();
            }
          }}
        >
          <ListItemIcon iconType="text-bubble" />
          <ListItemText primary="Contact Support" />
        </ListItemButton>
      </ListItem>

      {!isEmbedded && (
        <ListItem divider>
          <ListItemButton
            onClick={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              logAccountPageMenuItemTapped(USER_EVENTS.OPENED_HELP_CENTER);
              // Show intervention bottom sheet only if feature flag is enabled AND shift is in progress
              // Otherwise, use legacy flow (navigate to /needhelp)
              if (isTimeclockInfoEnabled && hasUpcomingOrInProgressShift) {
                onGetHelpClick();
              } else {
                history.push(`${match.url}/needhelp`);
              }
            }}
          >
            <ListItemIcon iconType="info" />
            <ListItemText primary="Get Help" secondary="Contact support or view help articles" />
          </ListItemButton>
        </ListItem>
      )}
    </>
  );
}
